/**
 * FormData payload builder utilities for equipment submission
 */

/**
 * Converts a URL to a File object by fetching the resource
 * @param {string} url - URL of the media file
 * @param {string} filename - Name for the file
 * @param {string} fileType - Type of the file (photo/video)
 * @returns {Promise<File>} - File object
 */
export const urlToFile = async (url, filename, fileType) => {
  try {
    const response = await fetch(url)
    const blob = await response.blob()

    // Determine MIME type from blob or file type
    let mimeType = blob.type
    if (!mimeType || mimeType === "application/octet-stream") {
      // Fallback to file extension or type
      if (fileType === "video" || url.match(/\.(mp4|mov|webm|ogg|m4v|quicktime|qt)($|\?)/i)) {
        mimeType = "video/mp4"
      } else {
        mimeType = "image/jpeg"
      }
    }

    return new File([blob], filename, { type: mimeType })
  } catch (error) {
    console.error("Error converting URL to File:", error)
    throw error
  }
}

/**
 * Converts URL-based media to File objects for duplicate mode
 * @param {Array} localMedia - Array of media objects
 * @returns {Promise<Array>} - Array of media objects with File objects
 */
export const convertMediaForDuplication = async (localMedia) => {
  const convertedMedia = await Promise.all(
    localMedia.map(async (media, idx) => {
      // Skip if already has file
      if (media.file || media.originFileObj) {
        return media
      }

      // Convert URL to File if media has URL but no file
      // This handles all existing media from original equipment
      if (media.url || media.preview_url) {
        try {
          const urlToConvert = media.url || media.preview_url
          const fileType = media.file_type || "photo"
          const extension = fileType === "video" ? "mp4" : "jpg"
          const filename = `media_${idx + 1}.${extension}`

          const file = await urlToFile(urlToConvert, filename, fileType)

          return {
            ...media,
            file,
            originFileObj: file
          }
        } catch (error) {
          console.error(`Failed to convert media ${idx}:`, error)
          // Return original media object if conversion fails
          return media
        }
      }

      return media
    })
  )

  return convertedMedia
}

/**
 * Builds properties attributes array for media/photos
 * @param {Array} localMedia - Array of media objects (should already be converted if needed)
 * @returns {Array} - Array of property attributes for FormData
 */
export const buildPropertiesAttributes = (localMedia) => {
  return localMedia.map((media, idx) => {
    if (media._destroy && media.ids) {
      return { Id: media.ids, _destroy: true }
    }
    return {
      file: media.file || media.originFileObj,
      file_type: media.file_type || media.type || "photo",
      position: idx + 1,
      Id: media.ids,
      // Include blob for video preview/poster image
      blob: media.blob
    }
  })
}

/**
 * Appends experiences (trips) to FormData
 * @param {FormData} formData - FormData object to append to
 * @param {Array} selectedExperiences - Array of selected experience IDs
 */
export const appendExperiences = (formData, selectedExperiences) => {
  if (selectedExperiences?.length === 0) {
    formData.append("boat[trip_ids][]", "")
    return
  }
  for (const id of selectedExperiences) {
    formData.append("boat[trip_ids][]", id)
  }
}

/**
 * Appends characteristics to FormData with support for user-created characteristics
 * Logic:
 * - Existing join record (has characteristicId): Send only `id` to preserve association
 * - New backend characteristic (in listing): Send only `boat_characteristic_id`
 * - New user-created characteristic: Send only `name` for backend creation
 *
 * @param {FormData} formData - FormData object to append to
 * @param {Array} selected - Array of selected characteristic objects: [{ id, characteristicId?, name }]
 * @param {Array} characteristicListing - Backend characteristic listing
 * @param {Array} removedCharacteristics - Array of removed characteristics to mark for destroy
 * @param {Array} userCreatedCharacteristics - Array of user-created characteristics: [{ id, name }]
 */
export const appendCharacteristics = (formData, selected, characteristicListing, removedCharacteristics, userCreatedCharacteristics) => {
  let selIdx = 0
  for (const item of selected) {
    const found = characteristicListing.find((c) => String(c.id) === String(item.id))
    const isUserCreated = userCreatedCharacteristics.some((uc) => String(uc.id) === String(item.id))

    if (item.characteristicId) {
      // Existing join record - send only id to preserve the association
      formData.append(`boat[characteristic_to_boats_attributes][${selIdx}][id]`, String(item.characteristicId))
    } else if (found) {
      // New backend characteristic - send only boat_characteristic_id
      formData.append(`boat[characteristic_to_boats_attributes][${selIdx}][boat_characteristic_id]`, String(found.id))
    } else if (isUserCreated) {
      // New user-created characteristic - send only name
      formData.append(`boat[characteristic_to_boats_attributes][${selIdx}][name]`, item.name)
    }
    selIdx++
  }

  let remIdx = 0
  for (const item of removedCharacteristics) {
    if (!item.characteristicId) continue
    const i = selected.length + remIdx
    formData.append(`boat[characteristic_to_boats_attributes][${i}][id]`, String(item.characteristicId))
    formData.append(`boat[characteristic_to_boats_attributes][${i}][_destroy]`, "true")
    remIdx++
  }
}

/**
 * Appends media properties to FormData
 * @param {FormData} formData - FormData object to append to
 * @param {Array} properties_attributes - Array of property attributes
 */
export const appendMedia = (formData, properties_attributes) => {
  for (let idx = 0; idx < properties_attributes.length; idx++) {
    const prop = properties_attributes[idx]
    if (prop._destroy) {
      if (prop.Id) {
        formData.append(`boat[properties_attributes][${idx}][id]`, prop.Id)
      }
      formData.append(`boat[properties_attributes][${idx}][_destroy]`, "true")
      continue
    }
    if (prop.file) {
      formData.append(`boat[properties_attributes][${idx}][file]`, prop.file)
    }
    if (prop?.Id) {
      formData.append(`boat[properties_attributes][${idx}][id]`, prop.Id)
    }
    // Add preview for videos (poster/thumbnail image)
    if ((prop.file_type === "video" || prop.file_type === "trimmed_video") && prop.blob) {
      formData.append(`boat[properties_attributes][${idx}][preview]`, prop.blob)
    }
    formData.append(`boat[properties_attributes][${idx}][file_type]`, prop.file_type)
    formData.append(`boat[properties_attributes][${idx}][position]`, prop.position)
  }
}

/**
 * Appends asset details to FormData
 * @param {FormData} formData - FormData object to append to
 * @param {Array} assets - Array of asset objects
 */
export const appendAssets = (formData, assets) => {
  let assetIdx = 0
  for (const asset of assets) {
    if (asset._destroy && asset.id) {
      formData.append(`boat[asset_details_attributes][${assetIdx}][id]`, asset.id)
      formData.append(`boat[asset_details_attributes][${assetIdx}][_destroy]`, "true")
      assetIdx++
      continue
    }
    if (asset.assetNumber?.trim() || asset.id || asset.available || !asset._destroy) {
      formData.append(`boat[asset_details_attributes][${assetIdx}][asset_number]`, asset.assetNumber?.trim() || "")
      formData.append(`boat[asset_details_attributes][${assetIdx}][available]`, asset.available ? "true" : "false")
      if (asset.id) {
        formData.append(`boat[asset_details_attributes][${assetIdx}][id]`, asset.id)
      }
      assetIdx++
    }
  }
}
